"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { ChevronRight, RefreshCw } from "lucide-react";
import { GlitterParticles } from "@/components/ui/glitter-particles";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";

interface StarWarsIntroProps {
  onShowDemo?: () => void;
}

export function StarWarsIntro({ onShowDemo }: StarWarsIntroProps) {
  const [stage, setStage] = useState<"idle" | "logo" | "crawl" | "exit" | "cta">("idle");
  const [key, setKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && stage === "idle") {
          // Add a small delay after coming into view before starting
          setTimeout(() => setStage("logo"), 800);
        }
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [stage]);

  useEffect(() => {
    if (stage === "idle" || stage === "cta") return;

    let t: NodeJS.Timeout;

    if (stage === "logo") {
      t = setTimeout(() => setStage("crawl"), 7500);
    } else if (stage === "crawl") {
      // Linear crawl for 30 seconds
      t = setTimeout(() => setStage("exit"), 30000);
    } else if (stage === "exit") {
      // Fade out crawl and wait before showing CTA
      t = setTimeout(() => setStage("cta"), 1200);
    }
    
    return () => {
      if (t) clearTimeout(t);
    };
  }, [stage, key]);

  const handleReplay = () => {
    setStage("logo");
    setKey(prev => prev + 1);
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-[600px] md:h-[800px] overflow-hidden text-white font-sans select-none my-24 border border-slate-900 shadow-2xl mx-auto max-w-7xl rounded-[3rem] bg-slate-900/40 backdrop-blur-sm"
    >
      {/* Background effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent" />
        <GlitterParticles />
      </div>

      {/* Logo Section - Keep active during crawl for smooth transition */}
      {stage !== "idle" && (
        <div className={`absolute inset-0 flex items-center justify-center z-20 overflow-hidden transition-opacity duration-1000 ${stage === "cta" ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
          <div key={key} className="animate-logo-zoom opacity-0 flex flex-col items-center scale-150">
             <Image 
               src="/logo.png" 
               alt="Storita Logo" 
               width={350} 
               height={350} 
               className="mb-8"
               priority
             />
             <h1 className="text-[8vw] font-black leading-none tracking-tighter text-center bg-gradient-to-b from-white to-slate-500 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(168,85,247,0.3)]">
                STORITA
             </h1>
             <p className="text-lg md:text-xl font-bold tracking-[0.6em] mt-8 text-purple-400 uppercase">A new era of intelligence</p>
          </div>
        </div>
      )}

      {/* The Crawl */}
      <div 
        className={`absolute inset-0 z-10 flex justify-center transition-opacity duration-700 ${stage === "crawl" ? "opacity-100" : "opacity-0"}`}
        style={{ perspective: "300px" }}
      >
        <div 
          className="relative w-[90%] max-w-3xl text-slate-300 font-medium text-justify md:text-center text-xl md:text-2xl leading-[1.8] transform-gpu"
          style={{
            transform: (stage === "crawl" || stage === "exit") ? "rotateX(15deg) translateY(-220%)" : stage === "cta" ? "rotateX(0deg) translateY(-250%)" : "rotateX(15deg) translateY(100%)",
            transition: (stage === "crawl" || stage === "exit") ? "transform 45s linear" : stage === "cta" ? "transform 2s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
            transformOrigin: "50% 100%",
          }}
        >
          <div className="space-y-12 pb-[10vh]">
            <div className="text-center mb-20 pt-10">
              <h2 className="text-xl md:text-2xl uppercase tracking-[0.4em] mb-4 text-purple-400/80">The Future of Commerce</h2>
              <h3 className="text-3xl md:text-5xl font-bold tracking-tight text-white italic underline decoration-purple-500/50 underline-offset-8">THE AUTONOMOUS WAVE</h3>
            </div>
            
            {/* ... lines omitted for brevity in thought, but I will include them in tool call if needed or just replace the whole section ... */}
            <p>
              The era of manual interpretation is over. 
              The sheer volume of signals generated 
              each day has outpaced the human ability 
              to process, understand, and react.
            </p>

            <p>
              In this sprawling digital economy, 
              merchants were forced to rely on 
              fragmented views and delayed 
              reports-remnants of a legacy 
              that could no longer keep pace.
            </p>

            <p>
              But a new movement has emerged. 
              Intelligence is no longer a tool 
              you use, but a teammate that 
              communicates.
            </p>

            <p className="text-white text-3xl md:text-4xl text-center py-6 font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent italic text-shadow-glow">
              The Data has Awakened.
            </p>

            <p className="text-slate-100 font-bold">
              The future belongs to those 
              who stop monitoring the past 
              and start conversing with the 
              future.
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA Container */}
      <div 
        className={`absolute inset-0 z-30 flex items-center justify-center transition-all duration-[1500ms] ease-out p-8 ${
          stage === "cta" ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="max-w-4xl w-full text-center space-y-12">
          <div className="space-y-6">
            <h2 className="text-4xl md:text-7xl font-black text-white tracking-tighter leading-none animate-in fade-in zoom-in duration-1000">
               THE AGE OF <br />
               <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-[length:200%_auto] animate-gradient-x bg-clip-text text-transparent">
                 INTELLIGENCE
               </span> <br />
               IS HERE.
            </h2>
            <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Your data is ready to talk. Stop building dashboards and start building your legacy.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <button
              onClick={onShowDemo}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-500 text-white px-12 py-5 rounded-2xl font-black text-xl transition-all hover:scale-105 shadow-[0_0_40px_rgba(168,85,247,0.4)] flex items-center justify-center group"
            >
              <span>Book a demo</span>
              <ChevronRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <WebsiteCtaLink href="/pricing" className="w-full sm:w-auto" fullWidth>
              Start Now
            </WebsiteCtaLink>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 right-8 z-50 flex items-center gap-4">
        {stage !== "cta" && (
          <button 
            onClick={() => setStage("cta")} 
            className="text-white/30 hover:text-white transition-colors text-xs tracking-widest uppercase border border-white/10 px-4 py-2 rounded-full backdrop-blur-sm"
          >
            Skip Intro
          </button>
        )}
        {(stage === "cta" || stage === "crawl") && (
          <button 
            onClick={handleReplay} 
            className="text-white/30 hover:text-white transition-colors text-xs tracking-widest uppercase border border-white/10 px-4 py-2 rounded-full backdrop-blur-sm flex items-center gap-2"
          >
            <RefreshCw className="h-3 w-3" />
            <span>Replay</span>
          </button>
        )}
      </div>

      <style jsx>{`
        .text-shadow-glow {
          text-shadow: 0 0 20px rgba(168, 85, 247, 0.5);
        }
        @keyframes fade-in-out {
          0% { opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-in-out {
          animation: fade-in-out 4s ease-in-out forwards;
        }
        @keyframes logo-zoom {
          0% { opacity: 0; transform: scale(1.5); }
          10% { opacity: 1; }
          95% { opacity: 1; }
          100% { opacity: 0; transform: scale(0.01); }
        }
        .animate-logo-zoom {
          animation: logo-zoom 10s cubic-bezier(0.12, 0, 0.39, 0) forwards;
        }
      `}</style>
    </div>
  );
}
